// Human labels for `order_events.event_type` (services/queue_events.py's
// taxonomy, spec §3). Anything not listed here or in NOUN_LABELS — a future
// event type, or a typo — falls back to a titleized slug rather than a raw
// snake_case string. `describeEvent` (SA-1020) is the ONE reading the
// Activity uses: process words, record words with their payload detail, the
// noun, the slug. shared/order-event-types.json lists every type the ledger
// writes, and eventLabels.test.ts fails on one with no words.
import { describeProcessEvent, INTENT_TEXT } from '@/components/process/processEventText'
import { dayText, lowerWords, outcomeWords } from '@/components/process/stepLiveText'
import { DECISION_LABELS } from '@/components/orders/feasibilityText'
import type { DecisionState } from '@/types/commerce'
import { formatCurrency } from '@/utils/currency'

export const EVENT_LABELS: Record<string, string> = {
  request_received: 'Request received',
  request_complete: 'Request complete',
  accepted_for_dispatch: 'Accepted for dispatch',
  analysis_started: 'Analysis started',
  clarification_requested: 'Clarification requested',
  clarification_received: 'Clarification received',
  first_technical_response: 'First technical response',
  specialist_review_requested: 'Specialist review requested',
  specialist_review_completed: 'Specialist review completed',
  revised_analysis_started: 'Revised analysis started',
  // A staff intake edit on a running process (SA-401 D31) writes this beside
  // `revised_analysis_started`; `describeProcessEvent` adds the detail line.
  // The two labels must agree (eventLabels.test.ts pins it).
  process_revision_applied: 'Process revised',
  intake_updated: 'Intake updated',
  final_feasibility_issued: 'Final feasibility issued',
  commercial_decision: 'Commercial decision',
  // SA-717 / SA-770: a person rejected the record as not an enquiry. The
  // close beside it reads "<Noun> closed" (NOUN_LABELS).
  record_rejected: 'Rejected as not an enquiry',
  // The process's own close, written when the record closes on a running
  // process (a rejection included); sentence case beside "Process
  // reinstated" (`describeProcessEvent`).
  process_closed: 'Process closed',
  queue_state_changed: 'Queue state changed',
  assignment_changed: 'Assignment changed',
  duplicate_check_completed: 'Duplicate check completed',
  duplicate_resolved: 'Duplicate resolved',
  // SA-1020 (in SA-1059): the twelve the Activity showed as titleized slugs.
  // `recordEventDetail` below adds the payload's detail line.
  process_started: 'Process started',
  process_step_entered: 'Step entered',
  process_task_overdue: 'Task overdue',
  process_timeout_unarmed: 'Timer not armed',
  quote_version_sent: 'Quote sent',
  quote_version_prepared: 'Quote prepared',
  quote_response_recorded: 'Customer response recorded',
  feasibility_recorded: 'Feasibility recorded',
  external_reference_added: 'Reference added',
  external_reference_removed: 'Reference removed',
  commercial_status_overridden: 'Status overridden',
  department_gates_derived: 'Department gates derived',
  department_gate_changed: 'Department gate changed',
}

export function titleizeSlug(slug: string): string {
  return slug
    .split('_')
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

// SA-1007: the labels that name the record take the deployment's record noun
// (`useRecordNoun().singular`): "Quote closed" on a charter operator, "Order
// closed" on a bakery, "Record closed" with no vocabulary pack. The stored
// value `mission_closed` is byte-pinned (SA-364); only its label reads the
// noun.
export const NOUN_LABELS: Record<string, (noun: string) => string> = {
  mission_closed: (noun) => `${noun} closed`,
  record_reinstated: (noun) => `${noun} reinstated`,
}

export function humanizeEventType(eventType: string, noun: string): string {
  if (NOUN_LABELS[eventType]) return NOUN_LABELS[eventType](noun)
  if (EVENT_LABELS[eventType]) return EVENT_LABELS[eventType]
  return titleizeSlug(eventType)
}

export interface EventText {
  label: string
  detail: string | null
}

const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null)
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)
/** An id as words with a capital: `propose_rate` → "Propose rate". */
const words = (v: unknown): string | null => {
  const id = str(v)
  if (!id) return null
  const w = lowerWords(id)
  return w.charAt(0).toUpperCase() + w.slice(1)
}
const join = (parts: Array<string | null | undefined | false>): string | null => {
  const kept = parts.filter((p): p is string => typeof p === 'string' && p !== '')
  return kept.length ? kept.join(' · ') : null
}
const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`

// `quote_response_recorded.response`: the STORED kind — order_quote.RESPONSE_KINDS
// (`counter` / `accepted` / `declined`; `countered` is the version's status, never
// the response) plus the two kinds only a harness `record_event` turn writes.
const RESPONSE_WORDS: Record<string, string> = {
  counter: 'countered',
  accepted: 'accepted',
  declined: 'declined',
  expired: 'expired',
  requote: 'requote asked',
}

// `quote_version_sent.via` (SA-1040 D1): how the prepared version became sent.
const VIA_WORDS: Record<string, string> = { email: 'by email', manual: 'recorded by hand' }

/**
 * The detail line for a record event the ledger writes outside the process
 * runtime (SA-1020). Payload keys are the backend's, snake_case as stored;
 * a missing or malformed key drops its clause, never the line's shape. Free
 * text a person typed (a note, a reason) is returned as plain text and the
 * Activity renders it as a text node.
 */
export function recordEventDetail(eventType: string, payload: Record<string, unknown> | null, now: Date = new Date()): string | null {
  if (!payload) return null
  switch (eventType) {
    case 'mission_closed':
    case 'process_closed':
      return outcomeWords(str(payload.outcome))
    case 'process_started':
      return words(payload.entry)
    case 'process_step_entered': {
      const exit = str(payload.via_exit)
      return join([words(payload.step), exit && `via “${exit}”`])
    }
    case 'process_task_overdue':
      return join([words(payload.step), words(payload.seat)])
    case 'process_timeout_unarmed': {
      const field = str(payload.field)
      return join([words(payload.step), field && `${lowerWords(field)} had no value`])
    }
    case 'quote_version_sent': {
      const version = num(payload.version)
      const amount = num(payload.amount_cents)
      const until = dayText(str(payload.valid_until), now)
      const via = str(payload.via)
      return join([version !== null && `v${version}`, amount !== null && formatCurrency(amount, str(payload.currency) ?? undefined),
        until && `valid until ${until}`, via && (VIA_WORDS[via] ?? lowerWords(via))])
    }
    case 'quote_version_prepared': {
      const version = num(payload.version)
      const amount = num(payload.amount_cents)
      const until = dayText(str(payload.valid_until), now)
      return join([version !== null && `v${version}`, amount !== null && formatCurrency(amount, str(payload.currency) ?? undefined),
        until && `valid until ${until}`,
        // SA-1436: an intake edit that changed what the PDF prints redid it.
        payload.document === true && (payload.intake === true ? 'document redone after the intake changed' : 'document rendered'),
        payload.draft === true && (payload.revised === true ? 'email reworded' : 'email drafted'),
        payload.revised === true && payload.draft !== true && payload.intake !== true && 'revised'])
    }
    case 'quote_response_recorded': {
      const version = num(payload.version)
      const response = str(payload.response)
      const amount = num(payload.amount_cents)
      // SA-1047 (spec §7): a response taken from the customer's email says so, and a read that differs from what was recorded says that too.
      const fromEmail = payload.from_email && typeof payload.from_email === 'object' ? payload.from_email as Record<string, unknown> : null
      const readAs = fromEmail && fromEmail.matched === false ? str(fromEmail.read_as) : null
      const readWords = readAs ? INTENT_TEXT.get(readAs) : undefined
      return join([version !== null && `v${version}`, response && (RESPONSE_WORDS[response] ?? lowerWords(response)), amount !== null && formatCurrency(amount),
        fromEmail && "from the customer's email", readWords && `read as ${readWords}`])
    }
    case 'feasibility_recorded': {
      const state = str(payload.decision_state)
      const count = num(payload.option_count)
      return join([state && (DECISION_LABELS[state as DecisionState] ?? words(state)), count !== null && plural(count, 'option')])
    }
    case 'external_reference_added':
    case 'external_reference_removed': {
      const system = str(payload.system)
      const reference = str(payload.reference)
      return system && reference ? `${system} ${reference}` : system ?? reference
    }
    case 'commercial_status_overridden': {
      const to = str(payload.to)
      return join([to ? `set to ${lowerWords(to)}` : 'override cleared', str(payload.note)])
    }
    case 'department_gates_derived': {
      const gates = payload.gates
      if (!gates || typeof gates !== 'object') return null
      return join(Object.entries(gates as Record<string, unknown>).map(([kind, state]) => {
        const k = words(kind)
        const s = str(state)
        return k && s ? `${k} ${lowerWords(s)}` : null
      }))
    }
    case 'department_gate_changed': {
      const department = words(payload.department)
      const to = str(payload.to)
      return join([department && to ? `${department}: ${lowerWords(to)}` : department, str(payload.reason)])
    }
    default:
      return null
  }
}

/**
 * ONE reading of a ledger row for the Activity (SA-1020): the process
 * runtime's words first (`describeProcessEvent`, the AI's hand and the
 * re-open and revision details), then the record events' words with their
 * payload detail, then the deployment's noun and, last, the titleized slug.
 * `ActivitySection` and `activitySummary` both read it, so the list and the
 * closed line never disagree.
 */
export function describeEvent(eventType: string, payload: Record<string, unknown> | null, noun: string, now: Date = new Date()): EventText {
  return describeProcessEvent(eventType, payload) ?? { label: humanizeEventType(eventType, noun), detail: recordEventDetail(eventType, payload, now) }
}
